import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface BooleanValueProps {
  value: boolean | null | undefined;
  /** Word shown for the true state. Defaults to "Yes". */
  trueLabel?: string;
  /** Word shown for the false state. Defaults to "No". */
  falseLabel?: string;
  /** Hide the word and render only the indicator (compact table use). */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Neutral boolean display used in read-only contexts (form view mode, table
 * cells). Deliberately avoids red/green valence: the true/false distinction is
 * carried by a filled-vs-hollow rounded square plus a word, both colorblind
 * safe. Edit mode uses a real (interactive) checkbox instead.
 */
export function BooleanValue({
  value,
  trueLabel = 'Yes',
  falseLabel = 'No',
  iconOnly = false,
  className,
}: BooleanValueProps) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <span
      className={cn('inline-flex items-center gap-2 text-sm', className)}
      role="img"
      aria-label={value ? trueLabel : falseLabel}
    >
      <span
        aria-hidden
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border',
          value
            ? 'border-foreground bg-foreground text-background'
            : 'border-muted-foreground/40 bg-transparent',
        )}
      >
        {value && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      {!iconOnly && (
        <span className={cn(!value && 'text-muted-foreground')}>
          {value ? trueLabel : falseLabel}
        </span>
      )}
    </span>
  );
}
